import {Button, useMediaQuery} from '@material-ui/core';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {changeDiscrepancies} from 'Agent/Accounts/ducks/accountDetail';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {Page} from 'Core/models/Page';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import Column from 'Shared/components/PagedView/Column';
import {withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {safeParseInt} from 'Shared/utilities/parsers';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {changeDiscrepancySummariesFilter, getDiscrepancySummaries} from '../ducks/discrepancySummary';
import {DiscrepancySummary} from '../models/AccountDetail';

type AccountDiscrepancySummariesProps = {
  selectedAgency: AgencyFilters;
};

const AccountDiscrepancySummaries = (props: AccountDiscrepancySummariesProps) => {
  const {selectedAgency} = props;
  const {encryptedIdentifier} = selectedAgency;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const match = useRouteMatch<{accountId: string}>();
  const accountId = match.params.accountId;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accountNumber = qs.parse(location.search).accountNumber as string;
  const sourceId = qs.parse(location.search).sourceId as string;
  const paymentDate = qs.parse(location.search).paymentDate as string;
  const {discrepancySummaries, discrepancySummaryFilter} = useSelector(
    (state: ReducerState) => state.discrepancySummary
  );
  const paymentDateConverted = new Date(paymentDate);

  useBreadcrumbs(accountNumber, match.url.replace('discrepancySummaries', '') + 'accountNumber/' + accountNumber);

  const getDiscrepancySummariesCurried = (encryptedIdentifier: string) => (accountNumber: string) => (
    paymentDate: Date
  ) => (sourceId: number) => (page?: Page<DiscrepancySummary>) =>
    getDiscrepancySummaries(encryptedIdentifier, accountNumber, sourceId, paymentDate, discrepancySummaryFilter, page);

  const getSummary = pipe(
    getDiscrepancySummariesCurried(encryptedIdentifier)(accountNumber)(paymentDateConverted),
    O.some,
    O.ap(safeParseInt(sourceId))
  );

  const paymentDateISOFormat =
    paymentDate !== undefined && paymentDate !== 'undefined'
      ? new Date(paymentDate).toISOString()
      : new Date().toISOString();

  const changeFilter = withValueChangedHook(discrepancySummaryFilter, changeDiscrepancySummariesFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: discrepancySummaryFilter,
  };

  useEffect(() => {
    pipe(
      getSummary,
      O.fold(
        () => {
          history.goBack();
        },
        retrieveSummaries => dispatch(retrieveSummaries())
      )
    );
  }, [dispatch, history, discrepancySummaryFilter]);

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredAccountDiscrepancySummaries',
    relativeFileLocation: `/api/employer/v1/accounts/discrepancysummaries/csv${addFilterParameters({
      ...discrepancySummaryFilter,
      accountNumber,
      sourceId,
      paymentDate: paymentDateISOFormat,
      encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <PagedView
      pagedViewKey="accountDiscrepancySummaries"
      loadMore={page => {
        pipe(
          getSummary,
          O.ap(O.some(page)),
          O.fold(() => {}, dispatch)
        );
      }}
      data={discrepancySummaries}
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
        )
      }
    >
      {columnProps => (
        <>
          <Column {...columnProps} title="Payment Date" default>
            {discrepancy => toFormattedDateAsUTC(discrepancy.paymentDate)}
          </Column>
          <Column
            {...columnProps}
            title="Payor Name"
            default
            filterProperties={withStringFilterProperties(filterProperties, 'payorName')}
          >
            {discrepancy => discrepancy.payorName}
          </Column>
          <Column {...columnProps} title="Paid Amount" default>
            {discrepancy => toMoneyValue(discrepancy.paidAmount)}
          </Column>
          <Column {...columnProps} title="Discrepancy Amount" default>
            {discrepancy => toMoneyValue(discrepancy.discrepancyAmount)}
          </Column>
          <Column {...columnProps} title="Bill Amount" default>
            {discrepancy => toMoneyValue(discrepancy.billAmount)}
          </Column>
          <Column {...columnProps} title="Discrepancies" default>
            {summary => (
              <Button
                color="primary"
                onClick={() => {
                  dispatch(changeDiscrepancies(O.some(summary.discrepancies!)));
                  history.push(AccountRouteConstants.ACCOUNT_DISCREPANCIES(accountNumber, accountId));
                }}
              >
                Details
              </Button>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default AccountDiscrepancySummaries;
